import Drawer from '@mui/material/Drawer'
import { readSharedArticle, setStatusSharedItem } from 'api/teams'
import { useAuthContext } from 'components/context/AuthProvider'
import { convertArticleTypeToPostType } from 'components/drawer/article/view/ViewArticleDrawer'
import DrawerContainer from 'components/drawer/common/DrawerContainer'
import NotificationSys from 'components/NotificationSystem'
import { defaultDrawerSX } from 'const'
import { useTeams } from 'features/Timeline/hooks/useTeams'
import { useEntityDataControl } from 'hooks/useEntityDataControl'
import { useCallback } from 'react'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from 'store/hooks'
import { metricClearUuid } from 'store/slice/metric.slice'
import { getSharedTimelineById, getSharedTimelineSilent } from 'store/slice/sharedTimeline.slice'
import { ArticleType, ETimelinePostStatus, ETimelinePostType, IMetricType } from 'types'
import { SharedArticleDrawerContent } from '../SharedArticleDrawerContent/SharedArticleDrawerContent'
import { SharedItemStatusChanger } from '../SharedItemStatusChanger/SharedItemStatusChanger'

type MapInfoByType = {
  [key in ArticleType]: {
    metricType: IMetricType
    timelinePostType: ETimelinePostType
    entityId: 'lessonId' | 'learnArticleId' | 'careArticleId'
  }
}

const mapInfoByType: MapInfoByType = {
  [ArticleType.Lesson]: {
    metricType: IMetricType.lesson,
    timelinePostType: ETimelinePostType.lessons,
    entityId: 'lessonId',
  },
  [ArticleType.LearnLibrary]: {
    metricType: IMetricType.learnArticle,
    timelinePostType: ETimelinePostType.learnArticles,
    entityId: 'learnArticleId',
  },
  [ArticleType.CareArticle]: {
    metricType: IMetricType.careArticle,
    timelinePostType: ETimelinePostType.careArticle,
    entityId: 'careArticleId',
  },
}

export function SharedArticleDrawer({
  isOpen,
  handleClose,
  id,
  type,
}: {
  isOpen: boolean
  handleClose: () => void
  id: number
  type: ArticleType
}) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { user } = useAuthContext()
  const { activeTeamId } = useTeams()

  const { data, loading, setData } = useEntityDataControl({
    id,
    loadFunc: () => readSharedArticle({ id }),
    warning: "Can't load the data",
  })

  const onChangeRoute = useCallback(
    (route: string) => {
      setData(null)
      navigate(route)
    },
    [navigate, setData],
  )

  const onDefaultClose = useCallback(
    (event?: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        return
      }

      dispatch(metricClearUuid({ type: mapInfoByType[type].metricType }))
      handleClose()
    },
    [dispatch, handleClose, type],
  )

  const onClose = useCallback(() => {
    dispatch(metricClearUuid({ type: mapInfoByType[type].metricType }))
    handleClose()
  }, [dispatch, handleClose, type])

  const setStatusMutation = useMutation(setStatusSharedItem)

  const handleSuccessStatusChange = (isSilent: boolean) => () => {
    if (activeTeamId === null || !user?.id) return

    if (!isSilent) {
      onDefaultClose()
      dispatch(getSharedTimelineById({ userId: user.id, teamId: activeTeamId }))
      NotificationSys.showSuccess(`Article successfully updated`)
    } else {
      dispatch(getSharedTimelineSilent({ userId: user.id, teamId: activeTeamId }))
    }
  }

  const onUpdateStatus = (status: ETimelinePostStatus, isSilent: boolean) => {
    const entityId = data ? data[mapInfoByType[type].entityId] : null
    if (!entityId) return

    setStatusMutation.mutate(
      {
        entityId,
        status,
        type: convertArticleTypeToPostType(type),
      },
      {
        onSuccess: handleSuccessStatusChange(isSilent),
      },
    )
  }

  return (
    <Drawer anchor="right" open={isOpen} onClose={onDefaultClose} sx={defaultDrawerSX}>
      <DrawerContainer
        isOpen={isOpen}
        hasData={!!data}
        loading={loading}
        handleClose={onDefaultClose}
      >
        {!loading && data && (
          <>
            <SharedItemStatusChanger status={data.status} onUpdateStatus={onUpdateStatus} />

            <SharedArticleDrawerContent
              data={data}
              type={type}
              handleClose={onClose}
              onUpdateStatus={onUpdateStatus}
              onChangeRoute={onChangeRoute}
            />
          </>
        )}
      </DrawerContainer>
    </Drawer>
  )
}
